"use client";

import LayoutWrapper from "@/components/layout/wrapper";
import CustomPagination from "@/components/card-pagination";
import SearchBox from "./search";
import Link from "next/link";
import Image from "next/image";
import { ImageIcon, PhoneCall } from "lucide-react";
import { usePaginationQuery } from "@/hook/pagination-query";
import ConsultantCardSkeleton from "./loading";
import { FallbackAvatar } from "@/components/fallback-avatar";

export default function ConsultantsCustomer({
  consultants,
  page,
  pageSize,
  searchParams,
}: any) {
  const { handlePageChange, handlePageSizeChange, isPending } =
    usePaginationQuery(searchParams);

  // Extracted function to render the main content
  const renderContent = () => {
    if (isPending) {
      return <SkeletonGrid />;
    }

    if (consultants?.results?.length) {
      return (
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-[#FAFBFC] p-2 rounded-2xl">
          {consultants.results.map((item: any) => (
            <Link
              key={item.id}
              href={`/customer/consultants/${item.id}`}
              className="flex items-center justify-center"
            >
              <div className="bg-white rounded-2xl p-1 md:p-8 h-72 w-full md:h-92 md:w-83 flex flex-row md:flex-col items-center justify-around md:justify-center text-center hover:shadow-lg transition">
                <FallbackAvatar
                  src={item.avatar}
                  name={item.name}
                  id={item.id}
                  alt={item.name}
                  className="w-32 h-32 mb-6"
                  textClassName="text-4xl"
                />
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold mb-5 text-gray-900">
                    {item?.name}
                  </h3>
                  <div className="flex flex-col gap-3">
                    <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                      <ImageIcon size={25} color="#99a1af" />
                      <span className="text-base font-bold text-gray-900 mr-3">
                        {item?.file_count}
                      </span>
                      <span>فایل فعال</span>
                    </span>

                    <span className="flex items-center gap-4 text-gray-500 text-sm sm:text-base">
                      <PhoneCall
                        color="#99a1af"
                        height={25}
                        className="rotate-250"
                      />
                      {item?.phone}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      );
    }

    // Empty state
    return (
      <div className="flex w-full justify-center items-center flex-col md:flex-row gap-10">
        <div className="pt-12 pl-10 text-center text-gray-500 text-lg font-medium">
          متاسفانه نتیجه‌ای پیدا نشد
        </div>

        <div className="relative md:w-125 md:h-125 w-75 h-75">
          <Image
            src="/assets/empty-properties.svg"
            alt="property ads not found"
            fill
            sizes="256px"
            style={{ objectFit: "contain" }}
          />
        </div>
      </div>
    );
  };

  return (
    <LayoutWrapper className="w-full">
      {/* Search */}
      <div className="w-full flex justify-center">
        <SearchBox defaultValue={searchParams?.s || ""} />
      </div>

      {/* Grid */}
      <div className="w-full flex justify-center mt-12">
        {renderContent()}
      </div>

      <CustomPagination
        page={page - 1}
        pageSize={pageSize}
        count={consultants?.count ?? 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        consultantsList={true}
      />
    </LayoutWrapper>
  );
}

const SKELETON_KEYS = ["ske-1", "ske-2", "ske-3", "ske-4", "ske-5", "ske-6", "ske-7", "ske-8"] as const;

function SkeletonGrid() {
  return (
    <div className="w-full flex justify-center my-12">
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-[#FAFBFC] p-1 rounded-2xl">
        {SKELETON_KEYS.map((key) => (
          <ConsultantCardSkeleton key={key} />
        ))}
      </div>
    </div>
  );
}